import React, { createContext, useReducer, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const LOCAL_STORAGE_KEY = "fullscreen-entries";

type FullsceenEntryState = {
    seenEntryIds: string[];
    availableEntryIds: string[];
    activeEntryId: string | null;
};

const DEFAULT_STATE: FullsceenEntryState = {
    seenEntryIds: [],
    availableEntryIds: [],
    activeEntryId: null,
};

type FullscreenEntryAction =
    | {
          type: "OPEN_FULLSCREEN_ENTRY";
          id: string;
      }
    | {
          type: "SET_AVAILABLE_ENTRY_IDS";
          forcedEntryId: string | null;
          ids: string[];
      }
    | {
          type: "CLOSE_FULLSCREEN_ENTRY";
          id: string;
      };

const FullsceenEntryContext = createContext<{
    state: FullsceenEntryState;

    actions: {
        openFullscreenEntry: (id: string) => void;
        closeFullscreenEntry: (id: string) => void;
    };
}>({
    state: DEFAULT_STATE,
    actions: {
        openFullscreenEntry: () => null,
        closeFullscreenEntry: () => null,
    },
});

const reducer = (
    state: FullsceenEntryState,
    action: FullscreenEntryAction,
): FullsceenEntryState => {
    switch (action.type) {
        case "OPEN_FULLSCREEN_ENTRY": {
            return {
                ...state,
                activeEntryId: action.id,
            };
        }
        case "SET_AVAILABLE_ENTRY_IDS": {
            const notShownEntry = action.ids.find(
                (id) => !state.seenEntryIds.includes(id),
            );

            const activeEntryId = action.forcedEntryId ?? notShownEntry ?? null;

            return {
                ...state,
                activeEntryId,
                availableEntryIds: action.ids,
            };
        }
        case "CLOSE_FULLSCREEN_ENTRY": {
            const seenEntryIds = [
                ...new Set([...state.seenEntryIds, action.id]),
            ];

            writeSeenEntryIdsToLocalStorage(seenEntryIds);

            return {
                ...state,
                seenEntryIds,
                activeEntryId: null,
            };
        }
        default:
            throw new Error("FullsceenEntryContext: Unhandled action type");
    }
};

export const FullscreenEntryContextProvider = ({
    children,
    availableEntryIds,
}: {
    children: JSX.Element | JSX.Element[];
    availableEntryIds: string[];
}): JSX.Element | null => {
    const initialState = DEFAULT_STATE;

    const seenEntryIds = readSeenEntryIdsFromLocalStorage();
    const notShownEntry = availableEntryIds.find(
        (id) => !seenEntryIds.includes(id),
    );

    const [searchParams] = useSearchParams();
    const entryId = searchParams.get("openFullscreenId");
    const activeEntryId = entryId ?? notShownEntry ?? null;

    const initialStateFromLocalStorage: FullsceenEntryState = {
        ...initialState,
        activeEntryId,
        seenEntryIds,
        availableEntryIds,
    };

    const openFullscreenEntry = (id: string) => {
        dispatch({ type: "OPEN_FULLSCREEN_ENTRY", id });
    };

    const closeFullscreenEntry = (id: string) => {
        dispatch({ type: "CLOSE_FULLSCREEN_ENTRY", id });
    };

    const [state, dispatch] = useReducer(reducer, initialStateFromLocalStorage);

    useEffect(() => {
        dispatch({
            type: "SET_AVAILABLE_ENTRY_IDS",
            ids: availableEntryIds,
            forcedEntryId: entryId,
        });
    }, [availableEntryIds]);

    return (
        <FullsceenEntryContext.Provider
            value={{
                state,
                actions: {
                    openFullscreenEntry,
                    closeFullscreenEntry,
                },
            }}
        >
            {children}
        </FullsceenEntryContext.Provider>
    );
};

export const useFullscreenEntryContext = () =>
    React.useContext(FullsceenEntryContext);

const readSeenEntryIdsFromLocalStorage = (): string[] => {
    try {
        const data = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) ?? "");
        const ids = data?.seenEntryIds ?? [];
        return ids;
    } catch {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        return [];
    }
};

const writeSeenEntryIdsToLocalStorage = (ids: string[]) => {
    localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify({
            seenEntryIds: ids,
        }),
    );
};
